import { useCallback } from "react";
import type { FundComplianceFileRef } from "@/lib/fund-compliance/api";
import {
  FundComplianceAuthError,
  uploadFundComplianceFile,
} from "@/lib/fund-compliance/api";
import type { ChatAction } from "@/hooks/useChatState";

/**
 * Build a placeholder attachment ref used while the real file is uploading.
 * The storage ids are temporary and replaced once the upload completes.
 */
export function createUploadingAttachment(file: File): FundComplianceFileRef {
  return {
    storageId: `uploading-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    storageName: "",
    filename: file.name,
    mimeType: file.type || "application/pdf",
    filesize: `${(file.size / 1024).toFixed(1)} KB`,
    isUploading: true,
  };
}

interface UseUploadFlowOptions {
  dispatch: React.Dispatch<ChatAction>;
  activeId: string | null;
  onUnauthorized: () => void;
}

/**
 * Handles file attachment to an active chat's draft slot.
 * When there is no active chat, the hook is a no-op — callers should handle
 * the empty-state attachment path directly (it needs to create the chat first).
 */
export function useUploadFlow({ dispatch, activeId, onUnauthorized }: UseUploadFlowOptions) {
  return useCallback(
    async (file: File) => {
      if (!activeId) return;

      dispatch({ type: "CLEAR_ERROR" });
      const uploadingAttachment = createUploadingAttachment(file);
      dispatch({ type: "ADD_DRAFT_ATTACHMENT", chatId: activeId, attachment: uploadingAttachment });

      try {
        const uploadedFile = await uploadFundComplianceFile(file);
        dispatch({
          type: "UPDATE_DRAFT_ATTACHMENT",
          chatId: activeId,
          oldStorageId: uploadingAttachment.storageId,
          newAttachment: uploadedFile,
        });
        dispatch({ type: "UPDATE_CONVERSATION_TYPE", chatId: activeId, conversationType: "review" });
      } catch (err) {
        dispatch({ type: "REMOVE_DRAFT_ATTACHMENT", chatId: activeId, storageId: uploadingAttachment.storageId });
        if (err instanceof FundComplianceAuthError) {
          onUnauthorized();
          return;
        }
        dispatch({ type: "BOOTSTRAP_ERROR", error: err instanceof Error ? err.message : "Unable to attach file." });
      }
    },
    [activeId, dispatch, onUnauthorized]
  );
}
